import { useMachine } from '@xstate/react';
import { FC, useEffect, useState } from 'react';

import { useCarmaError } from 'src/stores/errorStore';
import {
  DataLayerWatchlistEvent,
  pushToDataLayer,
  pushToDataLayerWatchList,
  pushToDataLayerWithEnhancedConversion,
} from 'src/utils/pushToDataLayer';

import { AuthFormMachineEventEnum, AuthFormMachineStateEnum, authFormMachine } from './AuthForm.machine';
import styles from './AuthForm.module.scss';
import { AuthFormDisplayState } from './AuthForm.types';
import { EmailForm } from './Forms/EmailForm';
import { ResendPasswordForm } from './Forms/ResendPasswordForm';
import { ResetPasswordForm } from './Forms/ResetPasswordForm';
import { SigninForm } from './Forms/SigninForm';
import { SignupPersonalDetailsForm } from './Forms/SignupPersonalDetailsForm';
import { SignupSuccess } from './Forms/SignupSuccess';
import { VerificationTokenForm } from './Forms/VerificationTokenForm';

interface Props {
  redirectUrl?: string;
  displayState: AuthFormDisplayState;
  onCancel: () => void;
  onSuccess: () => Promise<void>;
  /**
   * Check if the email exists
   * @returns `true` if the email exists, `false` otherwise
   */
  onSubmitEmail: (email: string) => Promise<boolean>;
  /**
   * Sign the user in
   * @returns `true` if successful, the error message otherwise
   */
  onSignin: (email: string, password: string) => Promise<true | string>;
  /**
   * Request a password reset
   * @returns `true` if successful, the error message otherwise
   */
  onResetPassword: (email: string) => Promise<true | string>;
  /**
   * Check if a phone number is taken
   * @returns `true` if number taken, `false` otherwise
   */
  onCheckPhoneNumberTaken: (phone: string) => Promise<boolean>;
  /**
   * Send a verification token
   * @returns the verification id
   */
  onSendVerificationToken: (phone: string) => Promise<string>;
  /**
   * Sign the user up
   * @returns `true` if successful, the error message otherwise
   */
  onSignup: (signupParams: {
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    password: string;
    marketing: boolean;
    verificationId: string;
    verificationCode: string;
  }) => Promise<true | string>;
}

export const AuthForm: FC<Props> = ({
  redirectUrl,
  displayState,
  onCancel,
  onSuccess,
  onSubmitEmail,
  onSignin,
  onResetPassword,
  onCheckPhoneNumberTaken,
  onSendVerificationToken,
  onSignup,
}) => {
  const [state, send, interpretService] = useMachine(authFormMachine, { actions: { onSuccess } });
  const getErrorMessage = useCarmaError();
  const [isWatchlist, setIsWatchlist] = useState<boolean>(false);

  const onBack = () => send(AuthFormMachineEventEnum.BACK);
  const onSubmitTracking = (success: boolean = false, reasons?: string[]) =>
    pushToDataLayer({
      event: `authentication_step-${state.value}-submitAttempt`,
      success,
      reasons,
    });

  useEffect(() => {
    const { searchParams } = new URL(window.location.href);
    setIsWatchlist(searchParams.get('type') === 'watchlist');
  }, []);

  useEffect(() => {
    const subscription = interpretService.subscribe((state) => {
      if (state.value === AuthFormMachineStateEnum.END) {
        pushToDataLayerWithEnhancedConversion(state.context.email!, state.context.phone, {
          event: `authentication_step-${state.value}`,
        });
      } else {
        pushToDataLayer({
          event: `authentication_step-${state.value}`,
        });
      }
    });

    return subscription.unsubscribe;
  }, [interpretService]);

  return (
    <section className={styles.root} data-testid={`state-${state.value}`}>
      {state.matches(AuthFormMachineStateEnum.START) && (
        <EmailForm
          onSubmit={async (values) => {
            const result = await onSubmitEmail(values.email);
            send(result ? AuthFormMachineEventEnum.EMAIL_EXISTS : AuthFormMachineEventEnum.EMAIL_DOES_NOT_EXIST, {
              email: values.email,
            });
            onSubmitTracking(true);
            return true;
          }}
          onBack={onCancel}
          onSubmitTracking={onSubmitTracking}
          displayState={displayState}
          email={state.context.email}
          redirectUrl={redirectUrl}
        />
      )}
      {state.matches(AuthFormMachineStateEnum.SIGNIN) && (
        <SigninForm
          onSubmit={async (values, helpers) => {
            const result = await onSignin(state.context.email, values.password);
            if (result === true) {
              onSubmitTracking(true);
              const { searchParams } = new URL(window.location.href);
              if (isWatchlist) {
                pushToDataLayerWatchList(DataLayerWatchlistEvent.watchlist_successful_add, {
                  triggerEvent: 'successful_login',
                  product: {
                    sku: searchParams.get('sku') || undefined,
                    title: searchParams.get('title') || undefined,
                    make: searchParams.get('make') || undefined,
                    model: searchParams.get('model') || undefined,
                    body: searchParams.get('body') || undefined,
                    transmission: searchParams.get('transmission') || undefined,
                    ancapRating: searchParams.get('ancapRating') || undefined,
                    buildYear: Number(searchParams.get('buildYear')) || undefined,
                    colour: searchParams.get('colour') || undefined,
                    price: searchParams.get('price') || undefined,
                  },
                });
              }
              send(AuthFormMachineEventEnum.SUCCESS);
            } else {
              const errorMessage = 'Invalid password';
              helpers.setFieldError('password', errorMessage);
              onSubmitTracking(false, [`api: ${errorMessage}`]);
            }

            return result;
          }}
          onBack={onBack}
          onSubmitTracking={onSubmitTracking}
          onClickResetPassword={() => send(AuthFormMachineEventEnum.FORGOT_PASSWORD)}
          email={state.context.email}
        />
      )}
      {state.matches(AuthFormMachineStateEnum.RESET_PASSWORD) && (
        <ResetPasswordForm
          onSubmit={async (values) => {
            const result = await onResetPassword(values.email);
            if (result === true) {
              onSubmitTracking(true);
              send(AuthFormMachineEventEnum.RESET_EMAIL_SENT, { email: values.email });
            }
            return result;
          }}
          onBack={onBack}
          onSubmitTracking={onSubmitTracking}
          email={state.context.email}
        />
      )}
      {state.matches(AuthFormMachineStateEnum.RESET_PASSWORD_CONFIRMATION) && (
        <ResendPasswordForm
          onSubmit={async () => {
            onSubmitTracking(true);
            const result = await onResetPassword(state.context.email);
            return result;
          }}
          onBack={onBack}
          onSubmitTracking={onSubmitTracking}
          email={state.context.email}
        />
      )}
      {state.matches(AuthFormMachineStateEnum.SIGNUP_PERSONAL_DETAILS) && (
        <SignupPersonalDetailsForm
          onSubmit={async (values, helpers) => {
            const isPhoneTaken = await onCheckPhoneNumberTaken(values.phone);
            if (isPhoneTaken) {
              const errorMessage = getErrorMessage('ct_phone_already_in_use', 'This phone number is already taken');
              helpers.setFieldError('phone', errorMessage);
              onSubmitTracking(false, [`api: ${errorMessage}`]);
              return errorMessage;
            }
            const verificationId = await onSendVerificationToken(values.phone);
            onSubmitTracking(true);
            send(AuthFormMachineEventEnum.SIGNUP_FORM_VALID, {
              firstName: values.firstName,
              lastName: values.lastName,
              phone: values.phone,
              password: values.newPassword,
              marketing: values.marketing,
              verificationId,
            });
            return true;
          }}
          onBack={onBack}
          onSubmitTracking={onSubmitTracking}
          email={state.context.email}
          firstName={state.context.firstName}
          lastName={state.context.lastName}
          phone={state.context.phone}
          newPassword={state.context.password}
          marketing={state.context.marketing}
        />
      )}
      {state.matches(AuthFormMachineStateEnum.SIGNUP_TOKEN_VERIFICATION) && (
        <VerificationTokenForm
          onSubmit={async (values, helpers) => {
            const result = await onSignup({
              ...state.context,
              verificationCode: values.verificationCode,
            });
            if (result === true) {
              onSubmitTracking(true);
              send(AuthFormMachineEventEnum.TOKEN_VERIFIED);
            } else {
              helpers.setFieldError('verificationCode', result);
              onSubmitTracking(false, [`api: ${result}`]);
            }
            return result;
          }}
          onBack={onBack}
          onSubmitTracking={onSubmitTracking}
          onResendTokenVerificationCode={async () => {
            const verificationId = await onSendVerificationToken(state.context.phone);
            send(AuthFormMachineEventEnum.RESEND_TOKEN, { verificationId });
          }}
          phone={state.context.phone}
        />
      )}
      {state.matches(AuthFormMachineStateEnum.SIGNUP_SUCCESSFUL) && (
        <SignupSuccess
          displayState={displayState}
          name={state.context.firstName}
          onSuccess={() => {
            if (isWatchlist) {
              pushToDataLayerWatchList(DataLayerWatchlistEvent.watchlist_successful_add, {
                triggerEvent: 'successful_account_creation',
              });
            }
            send(AuthFormMachineEventEnum.SUCCESS);
          }}
        />
      )}
    </section>
  );
};
